/**
* Voice search (Web Speech API) for theme product search inputs only.
* Binds to [data-voice-search-trigger] inside forms with input[type="search"][name="q"].
*/
(function () {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const UNSUPPORTED_TITLE = 'Voice search not supported';
  const MSG_PERMISSION_DENIED = 'Microphone access blocked. Enable it in browser settings.';
  const MSG_LISTENING = 'Listening…';
  const MSG_NO_SPEECH = "Didn't catch that, try again";
  const SILENCE_FALLBACK_MS = 4500;
  /** Debounce interim text → input + input events (limits Wizzy / predictive churn). */
  const INTERIM_INPUT_DEBOUNCE_MS = 260;

  /** Set when recognition fires not-allowed / service-not-allowed; cleared on successful start. */
  let isMicPermissionDenied = false;

  /** Cleared on each recognition `start`; set on speechstart/result only — not audiostart (mic open ≠ speech). */
  let heardSpeechOrAudioThisSession = false;
  /** Prevents duplicate "try again" for the same mic session (no-speech vs timer vs empty result). */
  let idleNoSpeechMessageShown = false;
  /** @type {number | null} */
  let silenceFallbackTimerId = null;
  /** @type {number | null} */
  let interimInputDebounceId = null;

  /** True when stopRecognition() ran (user toggled mic or switched field); false for silence-timer recognition.stop(). */
  let userInitiatedRecognitionStop = false;

  /** @type {SpeechRecognition | null} */
  let recognition = null;
  /** @type {{ input: HTMLInputElement; button: HTMLButtonElement; form: HTMLFormElement } | null} */
  let activeContext = null;
  /** @type {typeof activeContext} */
  let pendingContext = null;
  let listening = false;
 
 
  function getSearchInput(button) {
    const form = button.closest('form[role="search"]');
    if (!form) return null;
    const input = form.querySelector('input[type="search"][name="q"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** Anchor feedback to the search field (absolute; does not affect flex layout). */
  function getFeedbackHost(button) {
    return button.closest('.field') || button.parentElement;
  }
 
 
  function setListeningUI(button, isListening) {
    listening = isListening;
    button.classList.toggle('search__voice--listening', isListening);
    button.setAttribute('aria-pressed', isListening ? 'true' : 'false');
    if (isListening) {
      showListeningMessage(button);
    } else {
      hideListeningMessage(button);
    }
  }

  function hideListeningMessage(button) {
    const host = getFeedbackHost(button);
    if (!host) return;
    const el = host.querySelector('[data-voice-listening-feedback]');
    if (el) el.setAttribute('hidden', '');
  }

  function hideNoSpeechFeedback(button) {
    const host = getFeedbackHost(button);
    if (!host) return;
    const el = host.querySelector('[data-voice-nospeech-feedback]');
    if (el) {
      clearTimeout(el._voiceNoSpeechHideTid);
      el.setAttribute('hidden', '');
    }
  }

  function showNoSpeechFeedback(button) {
    hideListeningMessage(button);
    const host = getFeedbackHost(button);
    if (!host) return;
    const perm = host.querySelector('[data-voice-permission-tooltip]');
    if (perm) perm.setAttribute('hidden', '');
    let el = host.querySelector('[data-voice-nospeech-feedback]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-voice-nospeech-feedback', '');
      el.className = 'voice-search-permission-tooltip';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      host.appendChild(el);
    }
    el.textContent = MSG_NO_SPEECH;
    el.removeAttribute('hidden');
    clearTimeout(el._voiceNoSpeechHideTid);
    el._voiceNoSpeechHideTid = window.setTimeout(() => {
      el.setAttribute('hidden', '');
    }, 5000);
  }

  function showNoSpeechFeedbackOnce(button) {
    if (idleNoSpeechMessageShown) return;
    idleNoSpeechMessageShown = true;
    showNoSpeechFeedback(button);
  }

  function clearSilenceFallbackTimer() {
    if (silenceFallbackTimerId !== null) {
      clearTimeout(silenceFallbackTimerId);
      silenceFallbackTimerId = null;
    }
  }

  function clearInterimInputDebounce() {
    if (interimInputDebounceId !== null) {
      clearTimeout(interimInputDebounceId);
      interimInputDebounceId = null;
    }
  }

  function resetVoiceSessionIdleState() {
    heardSpeechOrAudioThisSession = false;
    idleNoSpeechMessageShown = false;
    userInitiatedRecognitionStop = false;
    clearSilenceFallbackTimer();
    clearInterimInputDebounce();
  }

  function showListeningMessage(button) {
    const host = getFeedbackHost(button);
    if (!host) return;
    const perm = host.querySelector('[data-voice-permission-tooltip]');
    if (perm) perm.setAttribute('hidden', '');
    const noSp = host.querySelector('[data-voice-nospeech-feedback]');
    if (noSp) noSp.setAttribute('hidden', '');
    let el = host.querySelector('[data-voice-listening-feedback]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-voice-listening-feedback', '');
      el.className = 'voice-search-listening-feedback';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      host.appendChild(el);
    }
    el.textContent = MSG_LISTENING;
    el.removeAttribute('hidden');
  }
 
 
  function showPermissionDeniedTooltip(button) {
    hideListeningMessage(button);
    hideNoSpeechFeedback(button);
    const host = getFeedbackHost(button);
    if (!host) return;
    let el = host.querySelector('[data-voice-permission-tooltip]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-voice-permission-tooltip', '');
      el.className = 'voice-search-permission-tooltip';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('hidden', '');
      host.appendChild(el);
    }
    el.textContent = MSG_PERMISSION_DENIED;
    el.removeAttribute('hidden');
    clearTimeout(el._voicePermHideTid);
    el._voicePermHideTid = window.setTimeout(() => {
      el.setAttribute('hidden', '');
    }, 4000);
  }

  function clearPredictiveSelection(input) {
    const host = input.closest('predictive-search');
    if (host && typeof host.closeResults === 'function') {
      host.closeResults(false);
    }
  }
 
 
  function applyTranscriptAndSubmit(input, button, transcript) {
    clearInterimInputDebounce();
    const trimmed = transcript.trim();
    if (!trimmed) {
      clearSilenceFallbackTimer();
      setListeningUI(button, false);
      showNoSpeechFeedbackOnce(button);
      return;
    }
 
 
    input.focus();
    input.value = trimmed;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
 
 
    clearPredictiveSelection(input);
 
 
    activeContext = null;
    pendingContext = null;
 
 
    const form = input.form;
    if (form) {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }
    setListeningUI(button, false);

    userInitiatedRecognitionStop = true;
    setTimeout(() => {
      try {
        if (!recognition) return;

        // restart recognition briefly
        recognition.start();

        // then stop like "no speech"
        setTimeout(() => {
          try {
            recognition.stop();
          } catch (e) {}
        }, 50);

      } catch (e) {}
    }, 50);
  }
 
 
  function createRecognition() {
    if (!SpeechRecognition) return null;
    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
 
 
    rec.addEventListener('start', () => {
      resetVoiceSessionIdleState();
      isMicPermissionDenied = false;
      if (activeContext) {
        silenceFallbackTimerId = window.setTimeout(() => {
          silenceFallbackTimerId = null;
          if (heardSpeechOrAudioThisSession || idleNoSpeechMessageShown || !activeContext) return;
          const btn = activeContext.button;
          setListeningUI(btn, false);
          showNoSpeechFeedbackOnce(btn);
          try {
            if (recognition) recognition.stop();
          } catch (e) {
            /* ignore */
          }
        }, SILENCE_FALLBACK_MS);
      }
      if (activeContext) setListeningUI(activeContext.button, true);
    });

    rec.addEventListener('speechstart', () => {
      heardSpeechOrAudioThisSession = true;
      clearSilenceFallbackTimer();
    });

    rec.addEventListener('result', (event) => {
      clearSilenceFallbackTimer();
      if (!activeContext) return;

      const lastIdx = event.results.length - 1;
      const last = event.results[lastIdx];
      const text = last[0] ? last[0].transcript : '';

      if (!last.isFinal) {
        if (text.trim()) {
          heardSpeechOrAudioThisSession = true;
        }
        clearInterimInputDebounce();
        interimInputDebounceId = window.setTimeout(() => {
          interimInputDebounceId = null;
          if (!activeContext) return;
          const input = activeContext.input;
          input.value = text;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }, INTERIM_INPUT_DEBOUNCE_MS);
        return;
      }

      clearInterimInputDebounce();
      if (text.trim()) {
        heardSpeechOrAudioThisSession = true;
      }
      applyTranscriptAndSubmit(activeContext.input, activeContext.button, text);
    });
 
 
    rec.addEventListener('error', (event) => {
      clearSilenceFallbackTimer();
      clearInterimInputDebounce();
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        isMicPermissionDenied = true;
        if (activeContext) setListeningUI(activeContext.button, false);
        return;
      }
      if (event.error === 'no-speech') {
        if (activeContext) {
          const btn = activeContext.button;
          setListeningUI(btn, false);
          showNoSpeechFeedbackOnce(btn);
        }
        return;
      }
      if (event.error === 'aborted') {
        if (activeContext) setListeningUI(activeContext.button, false);
        return;
      }
      if (activeContext) setListeningUI(activeContext.button, false);
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('Voice search:', event.error);
      }
    });

    rec.addEventListener('end', () => {
      clearSilenceFallbackTimer();
      clearInterimInputDebounce();
      const ctx = activeContext;
      if (
        ctx &&
        !heardSpeechOrAudioThisSession &&
        !idleNoSpeechMessageShown &&
        !userInitiatedRecognitionStop
      ) {
        showNoSpeechFeedbackOnce(ctx.button);
      }
      if (activeContext) setListeningUI(activeContext.button, false);
      activeContext = null;
      userInitiatedRecognitionStop = false;
      if (pendingContext) {
        const next = pendingContext;
        pendingContext = null;
        startRecognition(next.button, next.input, next.form);
      }
    });
 
 
    return rec;
  }
 
 
  function stopRecognition() {
    if (!recognition) return;
    userInitiatedRecognitionStop = true;
    try {
      recognition.abort();
    } catch (e) {
      try {
        recognition.stop();
      } catch (e2) {
        /* ignore */
      }
    }
  }
 
 
  function startRecognition(button, input, form) {
    if (!recognition) recognition = createRecognition();
    if (!recognition) return;
 
 
    if (listening && activeContext && activeContext.button === button) {
      stopRecognition();
      return;
    }
 
 
    if (listening && activeContext && activeContext.button !== button) {
      pendingContext = { input, button, form };
      stopRecognition();
      return;
    }
 
 
    activeContext = { input, button, form };
    recognition.lang = document.documentElement.lang || navigator.language || 'en';
 
 
    try {
      recognition.start();
    } catch (e) {
      activeContext = null;
      setListeningUI(button, false);
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('Voice search could not start', e);
      }
    }
  }
 
 
  function onTriggerClick(event) {
    const button = event.target.closest('[data-voice-search-trigger]');
    if (!button || !(button instanceof HTMLButtonElement)) return;
 
 
    const input = getSearchInput(button);
    if (!input || !input.form) return;
 
 
    event.preventDefault();
 
 
    if (!SpeechRecognition) return;

    if (isMicPermissionDenied) {
      showPermissionDeniedTooltip(button);
      return;
    }

    window.wizzyLazyAssetUtils?.enableWizzyAssets();

    document.body.classList.add("search-modal--open");

    const modal = document.querySelector("sticky-header search-modal details");
    if(modal) modal.setAttribute("open","true");

    startRecognition(button, input, input.form);
  }
 
 
  function initUnsupportedButtons() {
    document.querySelectorAll('[data-voice-search-trigger]').forEach((el) => {
      if (!(el instanceof HTMLButtonElement)) return;
      el.disabled = true;
      el.setAttribute('aria-disabled', 'true');
      el.title = UNSUPPORTED_TITLE;
    });
  }
 
 
  function init() {
    if (!SpeechRecognition) {
      initUnsupportedButtons();
      return;
    }
 
 
    document.addEventListener('click', onTriggerClick);
  }
 
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
 })();
 